import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { placeModel } from "../../api";
import "././PlaceDetailsPage.css";

// --- Breadcrumbs ---
const Breadcrumbs = ({ country, city, place }) => {
  const { t } = useTranslation();
  return (
    <nav className="breadcrumbs">
      <Link to={`/cities/${country?.CountryID}`}>{country?.CountryName}</Link>
      <span>›</span>
      <Link to={`/cityplaces/${city?.CityID}`}>{city?.CityName}</Link>
      <span>›</span>
      <span className="active">{place?.PlaceName}</span>
    </nav>
  );
};

// --- Hero Section ---
const PlaceHero = ({ image, name, city, country, category, rating, location }) => {
  const { t } = useTranslation();
  return (
    <section className="place-hero">
      <img src={image} alt={name} className="hero-img" />
      <div className="hero-content">
        <h1>{name}</h1>
        <div className="hero-meta">
          <span className="location">{city} / {country}</span>
          <span className="badge">{category}</span>
          {rating && <span className="rating">⭐ {rating}</span>}
        </div>
        <div className="hero-actions">
          {location?.x && location?.y ? (
            <a
              className="btn-map"
              href={`https://www.google.com/maps/search/?api=1&query=${location.x},${location.y}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("View on Map", "View on Map")}
            </a>
          ) : (
            <button className="btn-map" disabled>{t("View on Map", "View on Map")}</button>
          )}
        </div>
      </div>
    </section>
  );
};

// --- Info Card ---
const PlaceInfoCard = ({ category, price, hours, address, phone, website }) => {
  const { t } = useTranslation();
  return (
    <aside className="info-card">
      <div><strong>{t("Category", "Category")}:</strong> {category}</div>
      <div><strong>{t("Price Level", "Price Level")}:</strong> {price || "$$"}</div>
      <div><strong>{t("Opening Hours", "Opening Hours")}:</strong> {hours || t("N/A", "N/A")}</div>
      <div><strong>{t("Address", "Address")}:</strong> {address}</div>
      <div><strong>{t("Phone", "Phone")}:</strong> {phone}</div>
      {website && (
        <div>
          <strong>{t("Website", "Website")}:</strong>{" "}
          <a href={website} target="_blank" rel="noopener noreferrer">{website}</a>
        </div>
      )}
    </aside>
  );
};

// --- Gallery ---
const Gallery = ({ images }) => {
  const { t } = useTranslation();
  return (
    <div className="gallery">
      {images && images.length > 0 ? (
        images.map((img, idx) => (
          <div key={idx} className="gallery-item">
            <img src={img} alt={`Gallery ${idx}`} className="gallery-img" />
          </div>
        ))
      ) : (
        <div className="gallery-placeholder">{t("No images available", "No images available")}</div>
      )}
    </div>
  );
};

// --- Map Section ---
const MapSection = ({ location, name }) => {
  const { t } = useTranslation();
  return (
    <div className="map-section">
      {/* Placeholder for Google Maps/Leaflet */}
      <div className="map-placeholder">
        <span>{t("Map for", "Map for")} {name}</span>
        <span>
          {t("Lat", "Lat")}: {location?.x} | {t("Lng", "Lng")}: {location?.y}
        </span>
      </div>
    </div>
  );
};

// --- Related Place Card ---
const RelatedPlaceCard = ({ place }) => (
  <Link to={`/place-details/${place.PlaceID}`} className="related-card">
    <div className="related-img-wrapper">
      <img
        src={place.Images?.[0] || place.MainImageUrl}
        alt={place.PlaceName}
        className="related-img"
      />
    </div>
    <div className="related-info">
      <span className="badge">{place.CategoryName}</span>
      <h4>{place.PlaceName}</h4>
      <p>{place.Description?.slice(0, 60)}...</p>
    </div>
  </Link>
);

// --- Main Page ---
const PlaceDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { placeID } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const fetchPlace = async () => {
      try {
        // Expecting { place, similarPlaces } from API
        const { place, similarPlaces } = await placeModel(placeID);
        setPlace(place);
        setRelated(similarPlaces || []);
      } catch (err) {
        setPlace(null);
        setRelated([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlace();
  }, [placeID]);

  if (loading) return <div className="loading">{t("Loading...", "Loading...")}</div>;
  if (!place) return <div className="not-found">{t("Not found", "Not found")}</div>;

  return (
    <main className={`place-details-page page container ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      <Breadcrumbs
        country={{ CountryID: place.CountryID, CountryName: place.CountryName }}
        city={{ CityID: place.CityID, CityName: place.CityName }}
        place={place}
      />

      <PlaceHero
        image={place.Images?.[0]}
        name={place.PlaceName}
        city={place.CityName}
        country={place.CountryName}
        category={place.CategoryName}
        rating={place.Rating || "4.6"}
        location={place.Location}
      />

      <div className="details-layout">
        <section className="details-left">
          <div className="about-section">
            <h2>{t("About", "About")} {place.PlaceName}</h2>
            <p>{place.Description}</p>
            <p>
              {place.PlaceName} {t("is a top-rated", "is a top-rated")} {place.CategoryName} {t("in", "in")} {place.CityName}, {place.CountryName}.
              {t("Enjoy the vibrant atmosphere and authentic experience.", "Enjoy the vibrant atmosphere and authentic experience.")}
            </p>
          </div>
          <div className="why-visit-section">
            <h3>{t("Why Visit This Place?", "Why Visit This Place?")}</h3>
            <ul>
              <li>{t("Unique atmosphere", "Unique atmosphere")}</li>
              <li>{t("Great city views", "Great city views")}</li>
              <li>{t("Authentic local experience", "Authentic local experience")}</li>
            </ul>
          </div>
          <Gallery images={place.Images} />
          <MapSection location={place.Location} name={place.PlaceName} />
        </section>
        <aside className="details-right">
          <PlaceInfoCard
            category={place.CategoryName}
            price={place.PriceLevel}
            hours={place.OpeningHours}
            address={place.Address}
            phone={place.Phone}
            website={place.Website || place.Instagram}
          />
        </aside>
      </div>

      <section className="related-section">
        <h3>{t("Similar Places in This City", "Similar Places in This City")}</h3>
        <div className="related-grid">
          {related.map((p) => (
            <RelatedPlaceCard key={p.PlaceID} place={p} />
          ))}
        </div>
      </section>
    </main>
  );
};

export default PlaceDetailsPage;